import { memo, type FC } from "react";
import { m } from "framer-motion";
import { SymbolRow } from "./SymbolBox";
import { cardVariants } from "../lib/animations";
import { getCardSymbols } from "../utils/cardSymbols";
import { cn } from "../utils/helpers";
import type { AnswerCard as AnswerCardType } from "../types/game";

interface AnswerCardProps {
  card: AnswerCardType;
  selected?: boolean;
  selectable?: boolean;
  onClick?: () => void;
  width?: number;
  height?: number;
  className?: string;
  showLabel?: boolean;
  advancedMode?: boolean;
}

const AnswerCardComponent: FC<AnswerCardProps> = ({
  card,
  selected,
  selectable,
  onClick,
  width,
  height,
  className,
  showLabel = false,
  advancedMode = false,
}) => {
  const symbols = getCardSymbols(card.card_id);

  // Determine symbol size based on card dimensions
  const symbolSize =
    width && width < 80 ? "sm" : width && width < 120 ? "md" : "lg";

  // Determine text size based on card dimensions
  const textSize =
    width && width < 80
      ? "text-xs"
      : width && width < 120
        ? "text-sm"
        : "text-base";

  const style =
    width && height
      ? {
          width: `${width}px`,
          height: `${height}px`,
          minWidth: `${width}px`,
          minHeight: `${height}px`,
        }
      : {};

  return (
    <m.div
      className={cn(
        "relative flex flex-col justify-between rounded-neo-xs p-3 transition-all",
        "card-answer",
        !width && "min-w-[160px]",
        selectable && "cursor-pointer neo-hover-lift-md",
        className,
      )}
      style={{
        ...style,
        backgroundColor: "var(--color-surface)",
        border: selected
          ? "var(--neo-border-thick)"
          : "var(--neo-border-medium)",
        borderColor: selected ? "var(--color-primary)" : "var(--color-neutral)",
        boxShadow: selected ? "var(--neo-shadow-md)" : "var(--neo-shadow-sm)",
      }}
      onClick={selectable ? onClick : undefined}
      role={selectable ? "button" : undefined}
      tabIndex={selectable ? 0 : undefined}
      onKeyDown={(e) => {
        if (selectable && (e.key === "Enter" || e.key === " ")) {
          e.preventDefault();
          onClick?.();
        }
      }}
      variants={selectable ? cardVariants : undefined}
      initial="rest"
      whileHover={selectable ? "hover" : undefined}
      whileTap={selectable ? "tap" : undefined}
      animate={selected ? "selected" : "rest"}
    >
      {/* Top symbol row */}
      <SymbolRow symbols={symbols} size={symbolSize} />

      {/* Card text - centered and flexible */}
      <div className="flex-1 flex items-center justify-center py-2 overflow-hidden">
        <p
          className={cn("font-bold leading-tight text-center", textSize)}
          style={{ color: "var(--color-text-primary)" }}
        >
          {card.text}
        </p>
      </div>

      {/* Bottom symbol row */}
      <SymbolRow symbols={symbols} size={symbolSize} />

      {/* Optional label */}
      {showLabel && !width && (
        <div
          className="absolute bottom-1 right-2 text-xs font-bold uppercase tracking-wide"
          style={{ color: "var(--color-text-primary)" }}
        >
          Answer
        </div>
      )}

      {/* Card ID - shown in advanced mode */}
      {advancedMode && (
        <div
          className="absolute bottom-1 left-2 text-[10px] font-mono opacity-60"
          style={{ color: "var(--color-text-muted)" }}
          title={`Card ID: ${card.card_id}`}
        >
          {card.card_id}
        </div>
      )}
    </m.div>
  );
};

// Memoize to prevent re-renders of cards that haven't changed
export const AnswerCard = memo(AnswerCardComponent);

interface AnswerCardListProps {
  cards: AnswerCardType[];
  selectedIndices?: number[];
  onCardClick?: (index: number) => void;
  className?: string;
  emptyMessage?: string;
  advancedMode?: boolean;
}

export const AnswerCardList = memo(function AnswerCardList({
  cards,
  selectedIndices = [],
  onCardClick,
  className,
  emptyMessage = "No answer cards available",
  advancedMode = false,
}: AnswerCardListProps) {
  if (cards.length === 0) {
    return <div className="text-center text-gray-400 py-8">{emptyMessage}</div>;
  }

  return (
    <div
      className={cn(
        "grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4",
        className,
      )}
    >
      {cards.map((card, index) => (
        <AnswerCard
          key={card.card_id}
          card={card}
          selected={selectedIndices.includes(index)}
          selectable={!!onCardClick}
          onClick={() => onCardClick?.(index)}
          advancedMode={advancedMode}
        />
      ))}
    </div>
  );
});
